import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { readConsent, saveConsent, OPEN_EVENT } from "@/lib/consent";

const btn = "px-5 py-3 text-[11px] tracking-[0.22em] uppercase border transition-colors duration-300";
const CATS = [
  ["necessary", "Necessari", "Indispensabili al funzionamento del sito (es. memorizzare la tua scelta sui cookie, accesso all'area riservata). Sempre attivi."],
  ["statistics", "Statistici", "Misurano in forma aggregata l'uso del sito. Attualmente non utilizzati."],
  ["marketing", "Marketing", "Mostrano contenuti pubblicitari personalizzati. Attualmente non utilizzati."],
];

const Prefs = ({ prefs, setPrefs }) => (
  <div className="mt-5 space-y-4 border-t border-[#d4a73c]/20 pt-5">
    {CATS.map(([k, t, d]) => (
      <div key={k} className="flex gap-4 items-start justify-between">
        <div>
          <p className="text-sm text-[#f4f1ea]">{t}</p>
          <p className="text-xs text-[#bdb7ab] leading-relaxed mt-1">{d}</p>
        </div>
        <Switch data-testid={`cookie-toggle-${k}`} checked={k === "necessary" || !!prefs[k]} disabled={k === "necessary"}
          onCheckedChange={(v) => setPrefs({ ...prefs, [k]: v })} aria-label={t} />
      </div>
    ))}
  </div>
);

export const CookieBanner = () => {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [prefs, setPrefs] = useState({ statistics: false, marketing: false });

  useEffect(() => {
    const c = readConsent();
    if (!c) setOpen(true);
    const reopen = () => {
      const cur = readConsent();
      setPrefs({ statistics: !!cur?.statistics, marketing: !!cur?.marketing });
      setCustom(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, reopen);
    return () => window.removeEventListener(OPEN_EVENT, reopen);
  }, []);

  const done = (p) => {
    saveConsent(p);
    setOpen(false);
    setCustom(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div role="dialog" aria-label="Preferenze cookie" data-testid="cookie-banner"
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ duration: 0.5 }}
          className="fixed z-[60] bottom-0 inset-x-0 md:bottom-6 md:left-6 md:right-auto md:max-w-md bg-[#0d0d0e]/95 backdrop-blur-xl border border-[#d4a73c]/30 p-6 md:p-7 max-h-[85vh] overflow-y-auto">
          <button data-testid="cookie-close-btn" onClick={() => done({})} aria-label="Chiudi e rifiuta"
            className="absolute top-4 right-4 text-[#bdb7ab] hover:text-[#e8c063]"><X size={18} /></button>
          <p className="label">Cookie</p>
          <p className="mt-3 text-sm text-[#d8d2c6] leading-relaxed pr-4">
            Usiamo solo cookie tecnici necessari al funzionamento del sito. Con il tuo consenso potremmo usare in futuro
            cookie statistici o di marketing. Puoi accettare, rifiutare o personalizzare la scelta in qualsiasi momento.
            Leggi la <a href="/cookie-policy" className="text-[#e8c063] underline underline-offset-2">Cookie Policy</a> e
            la <a href="/privacy-policy" className="text-[#e8c063] underline underline-offset-2">Privacy Policy</a>.
          </p>
          {custom && <Prefs prefs={prefs} setPrefs={setPrefs} />}
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button data-testid="cookie-reject-btn" onClick={() => done({})} className={`${btn} border-[#d4a73c]/60 text-[#e8c063] hover:bg-[#d4a73c]/10`}>Rifiuta</button>
            <button data-testid="cookie-accept-btn" onClick={() => done({ statistics: true, marketing: true })} className={`${btn} border-[#d4a73c]/60 text-[#e8c063] hover:bg-[#d4a73c]/10`}>Accetta tutti</button>
            {custom ? (
              <button data-testid="cookie-save-btn" onClick={() => done(prefs)} className={`${btn} col-span-2 border-transparent bg-[#d4a73c] text-[#0d0d0e] hover:bg-[#e8c063]`}>Salva preferenze</button>
            ) : (
              <button data-testid="cookie-customize-btn" onClick={() => setCustom(true)} className="col-span-2 text-xs text-[#d8d2c6] underline underline-offset-4 hover:text-[#e8c063] py-1">Personalizza</button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
